import 'intl'
import 'intl/locale-data/jsonp/en'
import { INR } from '../core/constants'
import indianNumberFormat from './indianNumberFormat'

/**
 * TradeCred's Float parser that takes in a string and rounds it to two decimal places and returns float
 */
export const customParseFloat = str => {
  return parseFloat(str)?.toFixed(2)
}

/**
 * Custom Date Format - takes in datestring from ruby and converts it into human understandable date string
 * monthOptions - short, long
 */
export const customDateFormat = (dateString, monthOptions = 'long') => {
  // Making it null, incase the dateString is undefined it throws Invalid Date
  const date = new Date(dateString ? dateString : null)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: monthOptions,
    day: 'numeric',
  }).format(date)
}

export const customTimeFormat = dateString => {
  const date = new Date(dateString ? dateString : null)
  return new Intl.DateTimeFormat('en-US', { timeStyle: 'full', timeZone: 'Asia/Kolkata' }).format(date)
}

/**
 * Using compound interest to calculate maturity value A = P(1 + r)^n
 * 1000(1+ 0.12)^(90/365)=1028.33
 */
export const calcMaturityValue = (purchaseAmount, duration, netIRR) => {
  return customParseFloat(purchaseAmount * Math.pow(1 + netIRR / 100, duration / 365))
}

export const inputSanitation = val => {
  // Strips 4,00,00 and returns 40,000
  const newVal = indianNumberFormat(String(val).replace(/,/g, ''))
  // number_format handles characters in input to be '0', which resets input field
  return val ? (newVal === '0' ? '' : newVal) : ''
}

// Withdrawal cancellation window — matches backend WalletTransaction#is_cancellable? (3 hours).
export const isWithinCancellationWindow = transacted_on =>
  new Date() <= new Date(new Date(transacted_on).getTime() + 3 * 60 * 60 * 1000)

/**
 * Input 20000000
 * Output ₹ 2,00,00,000
 */
export const currencyParser = amount => {
  // Amounts are shown without decimals throughout the app.
  return `${INR}\u202F${indianNumberFormat(amount, 0)}`
}

/**
 * Calculates maturation date using the given duration
 * Returns string of date or Overdue in case of negative duration
 */
export const durationToDateParser = duration => {
  if (duration < 0) return 'Overdue'
  const date = new Date()
  date.setDate(date.getDate() + duration)
  return customDateFormat(date)
}

export const durationToDaysParser = duration => {
  if (duration < 0) return 'Overdue'
  return duration + ' days'
}

/**
 * Camel Case to Separate Capitalized words
 * camelCaseWohoo - Camel Case Wohoo
 */
export const camelToWords = camelCase => {
  const words = camelCase.replace(/([a-z])([A-Z])/g, '$1 $2')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Checks if elements contained in both arrays are equal
 */
export const arraysEqual = (a, b) => {
  if (a === b) return true
  if (a == null || b == null) return false
  if (a.length !== b.length) return false

  a.sort()
  b.sort()

  return a.every((val, idx) => val === b[idx])
}

export const removeSpaces = text => {
  return text.replace(/^\s+|\s+$/gm, '')
}

/**
 * Parses all the following into a singular string
  const test1 = {
    'keyaf': 'SDFSDF asdfaf'
  }
  const test2 = {
    'keyaf': ['SDFSDF asdfaf', 'adfafd adfad']
  }
  const test3 = {
    'keyaf': ''
  }
  const test4 = {
    'keyaf': {
      'key2': ['SDFSDF asdfaf', 'adfafd adfad']
    }, 
    'keydb': 'kasfkaf'
  }
  const test5 = 'erRes msesg'
 */
const flattenJSONObject = obj => {
  const flatValues = Object.values(obj).flatMap(val => {
    if (typeof val === 'object' && !Array.isArray(val)) {
      return flattenJSONObject(val)
    } else if (Array.isArray(val)) {
      return val
    } else {
      return [val]
    }
  })
  return flatValues.filter(Boolean)
}

/**
 * Summarizes and extracts errors from all sorts of nested json. Ignores keys and combines values if there are multiple values
 * Pass in the json rather than the error response
 */
export const errorSummary = error => {
  if (typeof error === 'string') {
    return humanize(error)
  }
  return humanize(flattenJSONObject(error).join(' , '))
}

/**
 * Humanizes string
 *
 * Removes any leading or trailing white space or underscores from the string.
 * Replaces any remaining white space or underscores with a single space character.
 * Capitalizes the first letter of the string.
 */
const humanize = str => {
  return str
    .replace(/^[\s_]+|[\s_]+$/g, '')
    .replace(/[_\s]+/g, ' ')
    .replace(/^[a-z]/, function (m) {
      return m.toUpperCase()
    })
}

// Convert bytes from File Object to readable size
export const bytesToHumanReadableSize = numberOfBytes => {
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  const exponent = Math.min(Math.floor(Math.log(numberOfBytes) / Math.log(1024)), units.length - 1)
  const approx = numberOfBytes / 1024 ** exponent

  return exponent === 0 ? `${numberOfBytes} bytes` : `${approx.toFixed(2)} ${units[exponent]}`
}

export const camelToTitleCase = str => {
  const withSpaces = str.replace(/([A-Z])/g, ' $1').split(' ')
  return withSpaces.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}
